// CoordTeachers.jsx — mirrors admin Teachers.jsx exactly
// All mutations go through coordinator request/approval workflow

import PasswordInput from '../components/PasswordInput';
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import ConfirmModal from '../components/ConfirmModal';
import {
  PencilIcon, PlusIcon, AcademicCapIcon, XMarkIcon, ClockIcon,
  ClipboardDocumentCheckIcon, CheckIcon,
  ArrowRightOnRectangleIcon, NoSymbolIcon, HomeIcon, MagnifyingGlassIcon,
  PaperAirplaneIcon, LockClosedIcon, LockOpenIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const parseDate = (d) => {
  if (!d) return '';
  if (typeof d === 'string') return d.slice(0, 10);
  const dt  = new Date(d);
  const y   = dt.getFullYear();
  const mo  = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return y + '-' + mo + '-' + day;
};

const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }).replace(/ /g, '-') : '';
const ini  = (n='') => n.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'T';

const formatPhone = (v) => {
  const digits = v.replace(/\D/g, '');
  if (digits.length <= 4) return digits;
  return digits.slice(0, 4) + '-' + digits.slice(4, 11);
};
const formatCnic = (v) => {
  const digits = v.replace(/\D/g, '');
  if (digits.length <= 5) return digits;
  if (digits.length <= 12) return digits.slice(0, 5) + '-' + digits.slice(5);
  return digits.slice(0, 5) + '-' + digits.slice(5, 12) + '-' + digits.slice(12, 13);
};

const REQ_BANNER = (
  <div style={{background:'#fff7ed',border:'1px solid #fed7aa',borderRadius:10,padding:'10px 14px',fontSize:'.76rem',color:'#c2410c',marginBottom:'1.2rem',display:'flex',alignItems:'center',gap:8}}>
    📋 Add, edit, assign, class teacher and exit actions require admin approval before taking effect.
  </div>
);

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .tr*{box-sizing:border-box;} .tr{font-family:'Sora',sans-serif;color:#1a1d2e;}
  .tr-hdr{display:flex;align-items:center;justify-content:space-between;margin-bottom:1.75rem;flex-wrap:wrap;gap:12px;}
  .tr-title{font-size:1.6rem;font-weight:700;letter-spacing:-.03em;color:#0f1123;}
  .tr-sub{font-size:.78rem;color:#9094a8;margin-top:2px;}
  .btn{padding:8px 18px;border-radius:10px;font-size:.8rem;font-weight:600;font-family:'Sora',sans-serif;cursor:pointer;border:none;display:inline-flex;align-items:center;gap:7px;transition:.15s;}
  .btn svg{width:15px;height:15px;stroke-width:2;flex-shrink:0;}
  .btn-green{background:#10c98e;color:white;} .btn-green:hover{box-shadow:0 4px 14px rgba(16,201,142,.35);}
  .btn-blue{background:#3b6ef8;color:white;} .btn-blue:hover{box-shadow:0 4px 14px rgba(59,110,248,.35);}
  .btn-teal{background:#0d9488;color:white;} .btn-teal:hover{box-shadow:0 4px 14px rgba(13,148,136,.35);}
  .btn-ghost{background:#f0f2f7;color:#6b7194;border:1px solid #e0e4f0;} .btn-ghost:hover{background:#e8ebf4;}
  .btn-amber{background:#fffbeb;color:#d97706;border:1px solid #fde68a;} .btn-amber:hover{background:#d97706;color:white;}
  .btn-red{background:#fef2f2;color:#ef4444;border:1px solid #fecaca;} .btn-red:hover{background:#ef4444;color:white;}
  .btn:disabled{opacity:.6;cursor:not-allowed;}
  .search-bar{display:flex;align-items:center;gap:8px;background:#f8f9fc;border:1.5px solid #e0e4f0;border-radius:10px;padding:7px 12px;flex:1;max-width:300px;}
  .search-bar:focus-within{border-color:#10c98e;background:white;}
  .search-bar svg{width:15px;height:15px;stroke:#9094a8;stroke-width:2;flex-shrink:0;}
  .search-inp{border:none;background:transparent;font-family:'Sora',sans-serif;font-size:.82rem;color:#1a1d2e;outline:none;width:100%;}
  .panel{background:white;border-radius:16px;border:1px solid #e8ebf4;overflow:hidden;margin-bottom:1rem;}
  .panel-hdr{display:flex;align-items:center;gap:10px;padding:16px 20px;border-bottom:1px solid #f0f2f8;}
  .panel-icon{width:34px;height:34px;border-radius:10px;background:#ecfdf5;color:#10c98e;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
  .panel-icon svg{width:17px;height:17px;stroke-width:1.8;}
  .tbl{width:100%;border-collapse:collapse;font-size:.8rem;}
  .tbl thead tr{background:#f8f9fc;border-bottom:1px solid #eaecf4;}
  .tbl th{padding:11px 16px;text-align:center !important;font-size:.67rem;font-weight:700;color:#9094a8;text-transform:uppercase;letter-spacing:.07em;white-space:nowrap;}
  .tbl th:last-child{text-align:right;}
  .tbl tbody tr{border-bottom:1px solid #f3f5fb;transition:background .12s;}
  .tbl tbody tr:last-child{border-bottom:none;} .tbl tbody tr:hover{background:#fafbff;}
  .tbl td{padding:11px 16px;color:#3a3f58;vertical-align:middle;} .tbl td:last-child{text-align:right;}
  .cards{display:none;padding:10px;}
  .card{background:#fafbff;border-radius:12px;border:1px solid #e8ebf4;padding:13px;margin-bottom:8px;}
  .card-top{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:8px;gap:8px;}
  .card-row{display:flex;justify-content:space-between;font-size:.76rem;padding:4px 0;border-bottom:1px solid #f0f2f8;gap:8px;}
  .card-row:last-of-type{border-bottom:none;}
  .card-label{color:#9094a8;font-weight:600;white-space:nowrap;}
  .card-actions{display:flex;gap:6px;margin-top:8px;flex-wrap:wrap;}
  .name-cell{display:flex;align-items:center;gap:10px;}
  .avatar{width:32px;height:32px;border-radius:9px;background:linear-gradient(135deg,#10c98e,#3b6ef8);display:flex;align-items:center;justify-content:center;font-size:.65rem;font-weight:700;color:white;flex-shrink:0;}
  .name-main{font-weight:600;color:#1a1d2e;font-size:.82rem;} .name-sub{font-size:.68rem;color:#9094a8;margin-top:1px;}
  .tag-wrap{display:flex;flex-wrap:wrap;gap:4px;}
  .tag{display:inline-block;padding:2px 8px;border-radius:99px;font-size:.67rem;font-weight:600;white-space:nowrap;}
  .tag-green{background:#ecfdf5;color:#10c98e;} .tag-purple{background:#f3f0ff;color:#8b5cf6;}
  .tag-teal{background:#f0fdfa;color:#0d9488;} .tag-more{background:#f0f2f7;color:#9094a8;}
  .mono{font-family:'JetBrains Mono',monospace;font-size:.75rem;color:#6b7194;}
  .mono-amt{font-family:'JetBrains Mono',monospace;font-size:.8rem;font-weight:500;color:#1a1d2e;}
  .status-pill{display:inline-flex;font-size:.67rem;font-weight:600;padding:2px 9px;border-radius:99px;}
  .status-pill.active{background:#ecfdf5;color:#10c98e;}
  .status-pill.resigned{background:#fef2f2;color:#ef4444;}
  .status-pill.terminated{background:#1a1d2e;color:white;}
  .act-btn{width:30px;height:30px;border-radius:8px;border:1px solid #e8ebf4;background:#f8f9fc;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;margin-left:4px;transition:.13s;}
  .act-btn svg{width:14px;height:14px;stroke-width:1.8;}
  .act-btn.assign{color:#10c98e;} .act-btn.assign:hover{background:#ecfdf5;border-color:#10c98e;}
  .act-btn.classteacher{color:#0d9488;} .act-btn.classteacher:hover{background:#f0fdfa;border-color:#0d9488;}
  .act-btn.edit{color:#3b6ef8;} .act-btn.edit:hover{background:#eef2ff;border-color:#3b6ef8;}
  .act-btn.resign{color:#d97706;} .act-btn.resign:hover{background:#fffbeb;border-color:#fde68a;}
  .act-btn.terminate{color:#ef4444;} .act-btn.terminate:hover{background:#fef2f2;border-color:#fecaca;}
  .act-btn.deactivate{color:#ef4444;} .act-btn.deactivate:hover{background:#fef2f2;border-color:#fecaca;}
  .act-btn.activate{color:#10c98e;} .act-btn.activate:hover{background:#ecfdf5;border-color:#10c98e;}
  .act-btn.history{color:#8b5cf6;} .act-btn.history:hover{background:#f3f0ff;border-color:#8b5cf6;}
  .act-btn.del{color:#ef4444;} .act-btn.del:hover{background:#fff1f1;border-color:#fecaca;}
  .empty{text-align:center;padding:52px 20px;color:#b0b5cc;font-size:.82rem;}
  .spin{width:16px;height:16px;border:2px solid #e4e7f0;border-top-color:#10c98e;border-radius:50%;animation:sp .7s linear infinite;display:inline-block;}
  @keyframes sp{to{transform:rotate(360deg);}}
  .pg{display:flex;align-items:center;justify-content:center;gap:6px;padding:16px 0;flex-wrap:wrap;}
  .pg-btn{padding:6px 14px;border-radius:9px;border:1px solid #e0e4f0;background:#f8f9fc;font-size:.78rem;font-weight:600;font-family:'Sora',sans-serif;color:#6b7194;cursor:pointer;}
  .pg-btn:hover:not(:disabled){background:#ecfdf5;border-color:#10c98e;color:#10c98e;} .pg-btn:disabled{opacity:.4;cursor:not-allowed;}
  .pg-info{font-family:'JetBrains Mono',monospace;font-size:.75rem;color:#9094a8;padding:0 8px;}
  .overlay{position:fixed;inset:0;background:rgba(10,12,28,.55);backdrop-filter:blur(3px);display:flex;align-items:center;justify-content:center;z-index:1000;padding:20px;}
  .modal{background:white;border-radius:20px;width:100%;max-height:90vh;border:1px solid #e8ebf4;box-shadow:0 24px 64px rgba(0,0,0,.18);overflow:hidden;display:flex;flex-direction:column;animation:mIn .25s ease;}
  @keyframes mIn{from{opacity:0;transform:translateY(16px) scale(.97)}to{opacity:1;transform:none}}
  .m-hdr{display:flex;align-items:center;justify-content:space-between;padding:18px 22px;border-bottom:1px solid #f0f2f8;flex-shrink:0;gap:8px;}
  .m-hdr-l{display:flex;align-items:center;gap:10px;min-width:0;}
  .m-icon{width:34px;height:34px;border-radius:10px;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
  .m-icon svg{width:17px;height:17px;stroke-width:1.8;}
  .m-icon.green{background:#ecfdf5;color:#10c98e;} .m-icon.blue{background:#eef2ff;color:#3b6ef8;}
  .m-icon.teal{background:#f0fdfa;color:#0d9488;}
  .m-icon.amber{background:#fffbeb;color:#d97706;} .m-icon.red{background:#fef2f2;color:#ef4444;}
  .m-title{font-size:.95rem;font-weight:700;color:#0f1123;} .m-sub{font-size:.72rem;color:#9094a8;margin-top:1px;}
  .m-close{width:30px;height:30px;border-radius:8px;border:1px solid #e8ebf4;background:#f8f9fc;display:flex;align-items:center;justify-content:center;cursor:pointer;color:#9094a8;flex-shrink:0;}
  .m-close svg{width:14px;height:14px;stroke-width:2;}
  .m-body{padding:22px;overflow-y:auto;flex:1;} .m-foot{display:flex;justify-content:flex-end;gap:8px;padding:16px 22px;border-top:1px solid #f0f2f8;flex-shrink:0;flex-wrap:wrap;}
  .fg{display:grid;grid-template-columns:1fr 1fr;gap:14px;}
  .ff{display:flex;flex-direction:column;gap:5px;} .ff.full{grid-column:1/-1;}
  .fl{font-size:.7rem;font-weight:700;color:#9094a8;text-transform:uppercase;letter-spacing:.06em;}
  .fi,.fs{font-family:'Sora',sans-serif;font-size:.82rem;padding:9px 12px;border:1.5px solid #e0e4f0;border-radius:10px;background:#f8f9fc;color:#1a1d2e;outline:none;width:100%;transition:.15s;}
  .fi:focus,.fs:focus{border-color:#10c98e;background:white;box-shadow:0 0 0 3px rgba(16,201,142,.08);}
  .fi.mono{font-family:'JetBrains Mono',monospace;}
  .fi[readonly]{background:#f0f2f7;color:#9094a8;cursor:not-allowed;border-color:#e8ebf4;}
  .fh{font-size:.68rem;color:#b0b5cc;}
  .fdiv{grid-column:1/-1;display:flex;align-items:center;gap:10px;margin:4px 0;}
  .fdiv-lbl{font-size:.65rem;font-weight:700;color:#c0c5dc;text-transform:uppercase;letter-spacing:.09em;white-space:nowrap;}
  .fdiv-line{flex:1;height:1px;background:#f0f2f8;}
  .auto-badge{font-size:.64rem;font-weight:600;padding:1px 7px;border-radius:99px;background:#ecfdf5;color:#10c98e;margin-left:6px;vertical-align:middle;}
  .readonly-badge{font-size:.64rem;font-weight:600;padding:1px 7px;border-radius:99px;background:#f0f2f7;color:#9094a8;margin-left:6px;vertical-align:middle;}
  .subj-grid{display:flex;flex-wrap:wrap;gap:6px;padding:6px 0;}
  .subj-btn{padding:4px 12px;border-radius:99px;border:1.5px solid #e0e4f0;font-size:.74rem;font-weight:600;cursor:pointer;font-family:'Sora',sans-serif;background:#f8f9fc;color:#6b7194;transition:.15s;}
  .subj-btn.on{background:#ecfdf5;color:#10c98e;border-color:#10c98e;}
  .cls-list{display:flex;flex-direction:column;gap:0;max-height:320px;overflow-y:auto;padding:4px;}
  .cls-item-wrap{border:1.5px solid #e0e4f0;border-radius:9px;background:#f8f9fc;transition:.15s;margin-bottom:6px;}
  .cls-item-wrap.has-on{border-color:#10c98e;}
  .cls-item-hdr{display:flex;align-items:center;gap:10px;padding:8px 12px;cursor:pointer;font-size:.8rem;color:#3a3f58;font-weight:500;user-select:none;}
  .cls-item-hdr:hover{background:#f0fdf8;}
  .cls-secs{display:flex;flex-wrap:wrap;gap:6px;padding:8px 12px 12px 40px;border-top:1px solid #e8ebf4;}
  .sec-btn{padding:4px 12px;border-radius:99px;border:1.5px solid #e0e4f0;font-size:.74rem;font-weight:600;cursor:pointer;font-family:'Sora',sans-serif;background:white;color:#6b7194;transition:.15s;}
  .sec-btn.on{background:#ecfdf5;color:#10c98e;border-color:#10c98e;}
  .sec-btn.on-all{background:#eef2ff;color:#3b6ef8;border-color:#3b6ef8;}
  .cls-item{display:flex;align-items:center;gap:10px;padding:8px 12px;border-radius:9px;border:1.5px solid #e0e4f0;background:#f8f9fc;cursor:pointer;font-size:.8rem;color:#3a3f58;font-weight:500;user-select:none;transition:.15s;}
  .cls-item.on{border-color:#10c98e;background:#ecfdf5;color:#0f7a5a;}
  .cls-item.ct-on{border-color:#0d9488;background:#f0fdfa;color:#0d9488;}
  .cls-chk{width:18px;height:18px;border-radius:5px;border:2px solid #e0e4f0;background:white;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:.15s;}
  .cls-chk svg{width:10px;height:10px;stroke-width:3;color:white;opacity:0;}
  .cls-item.on .cls-chk{background:#10c98e;border-color:#10c98e;}
  .cls-item.on .cls-chk svg{opacity:1;}
  .cls-item.ct-on .cls-chk{background:#0d9488;border-color:#0d9488;}
  .cls-item.ct-on .cls-chk svg{opacity:1;}
  .ct-radio{width:18px;height:18px;border-radius:50%;border:2px solid #e0e4f0;background:white;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:.15s;}
  .ct-radio-dot{width:8px;height:8px;border-radius:50%;background:#0d9488;opacity:0;transition:.15s;}
  .cls-item.ct-on .ct-radio{border-color:#0d9488;}
  .cls-item.ct-on .ct-radio-dot{opacity:1;}
  .info-box{background:#f0fdfa;border:1px solid #99f6e4;border-radius:10px;padding:10px 14px;font-size:.76rem;color:#0d7a6a;margin-bottom:14px;line-height:1.55;}
  .warn-box{background:#fffbeb;border:1px solid #fde68a;border-radius:10px;padding:10px 14px;font-size:.76rem;color:#92400e;margin-bottom:14px;line-height:1.55;}
  .exit-warn{background:#fffbeb;border:1px solid #fde68a;border-radius:10px;padding:10px 14px;font-size:.78rem;color:#92400e;margin-bottom:14px;}
  .exit-warn.red{background:#fef2f2;border-color:#fecaca;color:#991b1b;}
  .tr-fade{animation:trFade .4s ease both;}
  @keyframes trFade{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
  @media(max-width:768px){
    .tr-title{font-size:1.2rem;} .tbl-wrap{display:none!important;} .cards{display:block;}
    .m-body{padding:16px;} .m-foot{justify-content:stretch;} .m-foot .btn{flex:1;justify-content:center;}
    .fg{grid-template-columns:1fr;} .ff.full{grid-column:1;} .fdiv{grid-column:1;}
  }
`;

const FD = ({ label }) => <div className="fdiv"><span className="fdiv-lbl">{label}</span><div className="fdiv-line"/></div>;

export default function CoordTeachers() {
  const navigate = useNavigate();
  const [teachers,    setTeachers]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [page,        setPage]        = useState(1);
  const [total,       setTotal]       = useState(1);
  const [modal,       setModal]       = useState(false);
  const [editing,     setEditing]     = useState(null);
  const [assign,      setAssign]      = useState(null);
  const [ctModal,     setCtModal]     = useState(null);
  const [exitModal,   setExitModal]   = useState(null);
  const [exitForm,    setExitForm]    = useState({ exitDate:'', exitNote:'' });
  const [classes,     setClasses]     = useState([]);
  const [allSubjects, setAllSubjects] = useState([]);
  const [saving,      setSaving]      = useState(false);
  const [search,      setSearch]      = useState('');
  const [statusModal,     setStatusModal]     = useState(null); // { teacher, active }
  const [statusSaving,    setStatusSaving]    = useState(false);
  // The coordinator teachers list doesn't return login-active state, so we
  // track it locally per teacher id after a successful request (defaults active).
  const [statusOverrides, setStatusOverrides] = useState({});
  const searchTimer = useRef(null);
  const hasLoaded   = useRef(false);

  const emptyForm = {
    name:'', email:'', password:'', employeeId:'', biometricId:'',
    qualification:'', joiningDate:'', resignDate:'', monthlySalary:'',
    mobile:'', cnic:'', address:'',
    subjects:[], assignedClasses:[],
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { load(); loadClasses(); }, [page]);

  // Debounced search
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      if (page !== 1) setPage(1);
      else load();
    }, 350);
  }, [search]);

  useEffect(() => {
    api.get('/coordinator/subjects').then(r => { if (Array.isArray(r.data)) setAllSubjects(r.data); }).catch(() => {});
  }, []);

  const load = async () => {
    try {
      if (!hasLoaded.current) setLoading(true);
      const params = new URLSearchParams({ page });
      if (search) params.append('search', search);
      const { data } = await api.get('/coordinator/teachers?' + params.toString());
      setTeachers(data.teachers || (Array.isArray(data) ? data : []));
      setTotal(data.totalPages || 1);
      hasLoaded.current = true;
    } catch { toast.error('Failed to load teachers'); }
    finally { setLoading(false); }
  };

  const loadClasses = async () => {
    try { const { data } = await api.get('/coordinator/classes'); setClasses(Array.isArray(data) ? data : []); } catch {}
  };

  // ── Open Add ──────────────────────────────────────────────────────────────
  const openAdd = async () => {
    const today = new Date().toISOString().split('T')[0];
    setEditing(null);
    setForm({ ...emptyForm, joiningDate: today });
    setModal(true);
    try {
      const [eRes, bRes, clsRes, subjRes] = await Promise.all([
        api.get('/coordinator/teachers/next-employee-id'),
        api.get('/coordinator/teachers/next-biometric-id'),
        api.get('/coordinator/classes'),
        api.get('/coordinator/subjects'),
      ]);
      setForm(prev => ({
        ...prev,
        employeeId:  eRes.data.employeeId  || prev.employeeId,
        biometricId: bRes.data.biometricId || prev.biometricId,
      }));
      if (Array.isArray(clsRes.data))  setClasses(clsRes.data);
      if (Array.isArray(subjRes.data)) setAllSubjects(subjRes.data);
    } catch {}
  };

  // ── Open Edit ─────────────────────────────────────────────────────────────
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name:          t.user?.name    || '',
      email:         t.user?.email   || '',
      password:      '',
      employeeId:    t.employeeId    || '',
      biometricId:   t.biometricId   || '',
      qualification: t.qualification || '',
      joiningDate:   parseDate(t.joiningDate),
      resignDate:    parseDate(t.resignDate),
      monthlySalary: t.monthlySalary ?? t.salaryPerHour ?? '',
      mobile:        t.mobile        || '',
      cnic:          t.cnic          || '',
      address:       t.address       || '',
      subjects:      Array.isArray(t.subjects) ? [...t.subjects] : [],
      assignedClasses: (t.assignedClasses || []).map(c => String(c._id || c)),
    });
    setModal(true);
  };

  // ── Open Assign ───────────────────────────────────────────────────────────
  const openAssign = (t) => {
    const assignedSections = {};
    (t.assignedClasses || []).forEach(c => {
      const id = String(c._id || c);
      assignedSections[id] = c.sections?.length ? c.sections : 'all';
    });
    setAssign({ ...t, subjects: [...(t.subjects || [])], assignedSections });
  };

  // ── Open Class Teacher ────────────────────────────────────────────────────
  const openClassTeacher = async (t) => {
    try {
      const { data } = await api.get('/coordinator/classes');
      const allCls = Array.isArray(data) ? data : [];
      setClasses(allCls);
      const currentCT = allCls.find(c => c.classTeacher && (c.classTeacher._id || c.classTeacher).toString() === t._id.toString());
      setCtModal({
        teacher:    t,
        classId:    currentCT ? String(currentCT._id) : '',
        section:    currentCT?.sections?.[0] || '',
        allClasses: allCls,
      });
    } catch { toast.error('Failed to load classes'); }
  };

  // ── Open Exit ─────────────────────────────────────────────────────────────
  const openExit = (t, exitType) => {
    setExitModal({ teacher: t, exitType });
    setExitForm({ exitDate: new Date().toISOString().split('T')[0], exitNote: '' });
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const toggleSubject = (s) => {
    setForm(prev => {
      const cur = Array.isArray(prev.subjects) ? prev.subjects : [];
      return { ...prev, subjects: cur.includes(s) ? cur.filter(x => x !== s) : [...cur, s] };
    });
  };

  const toggleAssignSubject = (s) => {
    setAssign(prev => {
      const cur = Array.isArray(prev.subjects) ? prev.subjects : [];
      return { ...prev, subjects: cur.includes(s) ? cur.filter(x => x !== s) : [...cur, s] };
    });
  };

  const toggleAssignSection = (classId, section) => {
    const id = String(classId);
    setAssign(prev => {
      const cur     = prev.assignedSections || {};
      const curSecs = cur[id];
      if (section === 'all') {
        if (curSecs === 'all') { const next = {...cur}; delete next[id]; return {...prev, assignedSections: next}; }
        return {...prev, assignedSections: {...cur, [id]: 'all'}};
      }
      const existing = Array.isArray(curSecs) ? curSecs : [];
      const updated  = existing.includes(section) ? existing.filter(s => s !== section) : [...existing, section];
      if (updated.length === 0) { const next = {...cur}; delete next[id]; return {...prev, assignedSections: next}; }
      return {...prev, assignedSections: {...cur, [id]: updated}};
    });
  };

  const fmtSalary = (t) => {
    if (t.monthlySalary > 0) return `Rs. ${Number(t.monthlySalary).toLocaleString('en-PK')}/mo`;
    if (t.salaryPerHour > 0) return `Rs. ${t.salaryPerHour}/hr`;
    return '—';
  };

  const getStatus = (t) => {
    if (t.exitType === 'terminated') return 'terminated';
    if (t.exitType === 'resigned' || t.resignDate) return 'resigned';
    return 'active';
  };

  const getClassTeacherOf = (t) => t.classTeacherOf || null;

  const statusLabel = { active:'Active', resigned:'Resigned', terminated:'Terminated' };

  // ── Mutations → requests ──────────────────────────────────────────────────

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim())  { toast.error('Name is required'); return; }
    if (!form.email.trim()) { toast.error('Email is required'); return; }
    if (!editing && !form.password.trim()) { toast.error('Password is required'); return; }
    if (!form.employeeId.trim()) { toast.error('Employee ID is required'); return; }
    if (!form.joiningDate) { toast.error('Joining date is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        subjects:      form.subjects.filter(Boolean),
        monthlySalary: Number(form.monthlySalary) || 0,
        salaryPerHour: 0,
      };
      if (editing) {
        await api.post('/coordinator/request/teacher-edit', { teacherId: editing._id, ...payload });
        toast.success('Edit request submitted — awaiting admin approval');
      } else {
        await api.post('/coordinator/request/teacher-add', payload);
        toast.success('Add request submitted — awaiting admin approval');
      }
      setModal(false);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const submitAssign = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const classAssignments = Object.entries(assign.assignedSections || {}).map(([classId, sections]) => ({
        classId,
        sections: sections === 'all' ? [] : sections,
      }));
      await api.post('/coordinator/request/teacher-assign', {
        teacherId:        assign._id,
        teacherName:      assign.user?.name,
        subjects:         assign.subjects,
        classIds:         Object.keys(assign.assignedSections || {}),
        classAssignments,
      });
      toast.success('Assignment request submitted — awaiting admin approval');
      setAssign(null);
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const submitClassTeacher = async () => {
    if (ctModal.classId) {
      const cls = ctModal.allClasses.find(c => String(c._id) === ctModal.classId);
      const existingCT    = cls?.classTeacher;
      const isThisTeacher = existingCT && (existingCT._id || existingCT).toString() === ctModal.teacher._id.toString();
      if (existingCT && !isThisTeacher) {
        const ctName = existingCT?.user?.name || existingCT?.name || 'another teacher';
        const ok = window.confirm(`${cls.name} already has "${ctName}" as class teacher. Replace with ${ctModal.teacher.user?.name}? This will be sent as a request for admin approval.`);
        if (!ok) return;
      }
    }
    setSaving(true);
    try {
      await api.post('/coordinator/request/teacher-class-teacher', {
        teacherId:   ctModal.teacher._id,
        teacherName: ctModal.teacher.user?.name,
        classId:     ctModal.classId || null,
        section:     ctModal.section || null,
      });
      toast.success(ctModal.classId
        ? 'Class teacher request submitted — awaiting admin approval'
        : 'Remove class teacher request submitted — awaiting admin approval');
      setCtModal(null);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const submitExit = async () => {
    if (!exitForm.exitNote.trim()) { toast.error('Reason is required'); return; }
    setSaving(true);
    try {
      await api.post('/coordinator/request/teacher-exit', {
        teacherId:   exitModal.teacher._id,
        teacherName: exitModal.teacher.user?.name,
        employeeId:  exitModal.teacher.employeeId,
        exitType:    exitModal.exitType,
        exitDate:    exitForm.exitDate,
        exitNote:    exitForm.exitNote,
      });
      toast.success('Exit request submitted — awaiting admin approval');
      setExitModal(null);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const isTeacherActive = (t) => statusOverrides[t._id] !== undefined ? statusOverrides[t._id] : true;

  const openStatusModal = (t, active) => setStatusModal({ teacher: t, active });

  const confirmStatusAction = async () => {
    if (!statusModal) return;
    const { teacher, active } = statusModal;
    setStatusSaving(true);
    try {
      const { data } = await api.post('/coordinator/requests', {
        type: 'user_access',
        payload: { targetId: teacher.user_id, targetRole: 'teacher', active },
      });
      toast.success(data?.message || (active ? 'Teacher activated' : 'Deactivation request submitted'));
      setStatusOverrides(prev => ({ ...prev, [teacher._id]: active }));
      setStatusModal(null);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setStatusSaving(false); }
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{style}</style>
      <div className="tr tr-fade">
        <div className="tr-hdr">
          <div><h1 className="tr-title">Teachers</h1><p className="tr-sub">Manage teacher profiles, subjects and class assignments.</p></div>
          <button className="btn btn-green" onClick={openAdd}><PlusIcon />Add Teacher</button>
        </div>
        {REQ_BANNER}

        <div className="panel">
          <div className="panel-hdr" style={{flexWrap:'wrap',gap:10}}>
            <div style={{display:'flex',alignItems:'center',gap:10,flex:1}}>
              <div className="panel-icon"><AcademicCapIcon /></div>
              <div>
                <div style={{fontWeight:600,fontSize:'.88rem'}}>All Teachers</div>
                <div style={{fontSize:'.72rem',color:'#9094a8'}}>{loading ? 'Loading…' : teachers.length + ' teachers'}</div>
              </div>
            </div>
            <div className="search-bar">
              <MagnifyingGlassIcon/>
              <input className="search-inp" placeholder="Search by name or employee ID…"
                value={search} onChange={e => setSearch(e.target.value)}/>
            </div>
          </div>

          {loading
            ? <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:10,padding:'48px 0',color:'#9094a8',fontSize:'.82rem'}}><span className="spin"/>Loading…</div>
            : teachers.length === 0
            ? <div className="empty">No teachers yet.</div>
            : <>
                <div className="tbl-wrap" style={{overflowX:'auto'}}>
                  <table className="tbl">
                    <thead><tr>
                      <th style={{minWidth:160}}>Teacher</th>
                      <th style={{minWidth:100}}>Employee ID</th>
                      <th style={{minWidth:140,textAlign:'center'}}>Subjects</th>
                      <th style={{minWidth:130,textAlign:'center'}}>Classes</th>
                      <th style={{minWidth:130,textAlign:'center'}}>Class Teacher</th>
                      <th style={{minWidth:90,textAlign:'center'}}>Joining</th>
                      <th style={{minWidth:80,textAlign:'center'}}>Status</th>
                      <th style={{minWidth:100,textAlign:'center'}}>Salary</th>
                      <th style={{minWidth:100,textAlign:'center'}}>Resign Date</th>
                      <th style={{textAlign:'right',minWidth:120}}>Actions</th>
                    </tr></thead>
                    <tbody>
                      {teachers.map(t => {
                        const status = getStatus(t);
                        const ctOf   = getClassTeacherOf(t);
                        return (
                          <tr key={t._id}>
                            <td>
                              <div className="name-cell">
                                <div className="avatar">{ini(t.user?.name)}</div>
                                <div><div className="name-main">{t.user?.name}</div><div className="name-sub">{t.qualification || t.user?.email}</div></div>
                              </div>
                            </td>
                            <td><span className="mono">{t.employeeId}</span></td>
                            <td style={{textAlign:'center'}}>
                              <div style={{display:'flex',flexDirection:'column',gap:3,alignItems:'center'}}>
                                {(t.subjects||[]).length === 0
                                  ? <span style={{fontSize:'.72rem',color:'#c0c5dc'}}>—</span>
                                  : (t.subjects||[]).map(s => <span key={s} className="tag tag-green">{s}</span>)}
                              </div>
                            </td>
                            <td style={{textAlign:'center'}}>
                              <div style={{display:'flex',flexDirection:'column',gap:3,alignItems:'center'}}>
                                {(t.assignedClasses||[]).length === 0
                                  ? <span style={{fontSize:'.72rem',color:'#c0c5dc'}}>—</span>
                                  : (t.assignedClasses||[]).map(c => {
                                      const secs = c.sections?.length ? ' · Sec ' + c.sections.join(', ') : '';
                                      return <span key={c._id || c} className="tag tag-purple">{(c.name || 'Unknown Class') + secs}</span>;
                                    })}
                              </div>
                            </td>
                            <td style={{textAlign:'center'}}>
                              {ctOf
                                ? <span className="tag tag-teal">🏠 {ctOf.name}{ctOf.section ? ' · Sec ' + ctOf.section : ''}</span>
                                : <span style={{fontSize:'.72rem',color:'#c0c5dc'}}>—</span>}
                            </td>
                            <td style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.74rem',color:'#6b7194',textAlign:'center'}}>{fmtD(t.joiningDate) || '—'}</td>
                            <td style={{textAlign:'center'}}><span className={`status-pill ${status}`}>{statusLabel[status]}</span></td>
                            <td style={{textAlign:'center'}}><span className="mono-amt">{fmtSalary(t)}</span></td>
                            <td style={{textAlign:'center'}}><span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.74rem',color:t.resignDate?'#ef4444':'#c0c5dc'}}>{t.resignDate ? fmtD(t.resignDate) : '—'}</span></td>
                            <td>
                              <button className="act-btn assign"      title="Request: Assign Subjects & Classes" onClick={() => openAssign(t)}><ClipboardDocumentCheckIcon /></button>
                              <button className="act-btn classteacher" title="Request: Set Class Teacher"         onClick={() => openClassTeacher(t)}><HomeIcon /></button>
                              <button className="act-btn edit"         title="Request: Edit"                      onClick={() => openEdit(t)}><PencilIcon /></button>
                              <button className="act-btn history"      title="Full History"                       onClick={() => navigate(`/coordinator/staff-history/${t.user?._id}?role=teacher&isPortal=true`)}><ClockIcon /></button>
                              <button className="act-btn resign"       title="Request: Mark Resigned"             onClick={() => openExit(t, 'resigned')}><ArrowRightOnRectangleIcon /></button>
                              <button className="act-btn terminate"    title="Request: Mark Terminated"           onClick={() => openExit(t, 'terminated')}><NoSymbolIcon /></button>
                              {isTeacherActive(t)
                                ? <button className="act-btn deactivate" title="Request: Deactivate" onClick={() => openStatusModal(t, false)}><LockClosedIcon /></button>
                                : <button className="act-btn activate" title="Request: Activate" onClick={() => openStatusModal(t, true)}><LockOpenIcon /></button>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="cards">
                  {teachers.map(t => {
                    const status = getStatus(t);
                    const ctOf   = getClassTeacherOf(t);
                    return (
                      <div key={t._id} className="card">
                        <div className="card-top">
                          <div className="name-cell">
                            <div className="avatar">{ini(t.user?.name)}</div>
                            <div><div className="name-main">{t.user?.name}</div><div className="name-sub">{t.user?.email}</div></div>
                          </div>
                          <span className={`status-pill ${status}`}>{statusLabel[status]}</span>
                        </div>
                        <div className="card-row"><span className="card-label">Employee ID</span><span className="mono">{t.employeeId}</span></div>
                        <div className="card-row"><span className="card-label">Subjects</span><div className="tag-wrap">{(t.subjects||[]).map(s => <span key={s} className="tag tag-green">{s}</span>)}</div></div>
                        <div className="card-row"><span className="card-label">Class Teacher</span>{ctOf ? <span className="tag tag-teal">🏠 {ctOf.name}{ctOf.section ? ' · Sec ' + ctOf.section : ''}</span> : <span style={{color:'#c0c5dc',fontSize:'.72rem'}}>—</span>}</div>
                        <div className="card-row"><span className="card-label">Salary</span><span className="mono-amt">{fmtSalary(t)}</span></div>
                        <div className="card-row"><span className="card-label">Joining</span><span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.72rem',color:'#6b7194'}}>{fmtD(t.joiningDate) || '—'}</span></div>
                        <div className="card-actions">
                          <button className="act-btn assign"       style={{width:'auto',padding:'0 10px',gap:4,fontSize:'.72rem'}} onClick={() => openAssign(t)}><ClipboardDocumentCheckIcon />Assign</button>
                          <button className="act-btn classteacher" style={{width:'auto',padding:'0 10px',gap:4,fontSize:'.72rem'}} onClick={() => openClassTeacher(t)}><HomeIcon />Class Teacher</button>
                          <button className="act-btn edit"      onClick={() => openEdit(t)}><PencilIcon /></button>
                          <button className="act-btn history"   title="Full History" onClick={() => navigate(`/coordinator/staff-history/${t.user?._id}?role=teacher&isPortal=true`)}><ClockIcon /></button>
                          <button className="act-btn resign"    onClick={() => openExit(t, 'resigned')}><ArrowRightOnRectangleIcon /></button>
                          <button className="act-btn terminate" onClick={() => openExit(t, 'terminated')}><NoSymbolIcon /></button>
                          {isTeacherActive(t)
                            ? <button className="act-btn deactivate" title="Request: Deactivate" onClick={() => openStatusModal(t, false)}><LockClosedIcon /></button>
                            : <button className="act-btn activate" title="Request: Activate" onClick={() => openStatusModal(t, true)}><LockOpenIcon /></button>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
          }
          {total > 1 && (
            <div className="pg">
              <button className="pg-btn" onClick={() => setPage(p => Math.max(1, p-1))} disabled={page===1}>← Prev</button>
              <span className="pg-info">Page {page} of {total}</span>
              <button className="pg-btn" onClick={() => setPage(p => Math.min(total, p+1))} disabled={page===total}>Next →</button>
            </div>
          )}
        </div>
      </div>

      {/* ══ Add / Edit Modal ══════════════════════════════════════════════════ */}
      {modal && createPortal(
        <div className="overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal" style={{maxWidth:580}}>
            <div className="m-hdr">
              <div className="m-hdr-l">
                <div className="m-icon green"><AcademicCapIcon /></div>
                <div>
                  <div className="m-title">{editing ? 'Request: Edit Teacher' : 'Request: Add Teacher'}</div>
                  <div className="m-sub">Sent to admin for approval</div>
                </div>
              </div>
              <button className="m-close" onClick={() => setModal(false)}><XMarkIcon /></button>
            </div>
            <form onSubmit={submit} style={{display:'contents'}} autoComplete="off">
              <div className="m-body">
                <div className="fg">
                  <FD label="Account" />
                  <div className="ff"><label className="fl">Full Name *</label><input className="fi" value={form.name} onChange={e => setForm(p=>({...p,name:e.target.value}))} required /></div>
                  <div className="ff"><label className="fl">Email *</label><input type="email" className="fi" value={form.email} onChange={e => setForm(p=>({...p,email:e.target.value}))} required /></div>
                  {!editing && <div className="ff full"><label className="fl">Password *</label><PasswordInput className="fi" autoComplete="new-password" value={form.password} onChange={e => setForm(p=>({...p,password:e.target.value}))} required /></div>}

                  <FD label="Professional Details" />
                  <div className="ff">
                    <label className="fl">Employee ID <span className="auto-badge">auto</span><span className="readonly-badge">read-only</span></label>
                    <input className="fi mono" value={form.employeeId} readOnly />
                  </div>
                  <div className="ff">
                    <label className="fl">Biometric ID <span className="auto-badge">auto</span><span className="readonly-badge">read-only</span></label>
                    <input className="fi mono" value={form.biometricId} readOnly />
                  </div>
                  <div className="ff"><label className="fl">Qualification</label><input className="fi" value={form.qualification} onChange={e => setForm(p=>({...p,qualification:e.target.value}))} /></div>
                  <div className="ff">
                    <label className="fl">Monthly Salary (Rs.)</label>
                    <input type="number" min="0" className="fi mono" placeholder="e.g. 25000" value={form.monthlySalary} onChange={e => setForm(p=>({...p,monthlySalary:e.target.value}))} />
                    <span className="fh">Fixed monthly salary — used for payroll calculation</span>
                  </div>
                  <div className="ff">
                    <label className="fl">Joining Date *</label>
                    <input type="date" className="fi" value={form.joiningDate} onChange={e => setForm(p=>({...p,joiningDate:e.target.value}))} required />
                  </div>
                  <div className="ff">
                    <label className="fl">Resign Date</label>
                    <input type="date" className="fi" value={form.resignDate} onChange={e => setForm(p=>({...p,resignDate:e.target.value}))} />
                    {form.resignDate
                      ? <span className="fh" style={{color:'#ef4444'}}>{fmtD(form.resignDate + 'T00:00:00')} — will show Resigned</span>
                      : <span className="fh">Leave blank if still employed.</span>}
                  </div>

                  <FD label="Contact Information" />
                  <div className="ff">
                    <label className="fl">Mobile</label>
                    <input className="fi mono" placeholder="0300-1234567" value={form.mobile}
                      onChange={e => setForm(p=>({...p,mobile:formatPhone(e.target.value)}))} maxLength={12} />
                  </div>
                  <div className="ff">
                    <label className="fl">CNIC</label>
                    <input className="fi mono" placeholder="12345-1234567-1" value={form.cnic}
                      onChange={e => setForm(p=>({...p,cnic:formatCnic(e.target.value)}))} maxLength={15} />
                  </div>
                  <div className="ff full">
                    <label className="fl">Address</label>
                    <input className="fi" placeholder="House/Street, City" value={form.address}
                      onChange={e => setForm(p=>({...p,address:e.target.value}))} />
                  </div>

                  <FD label="Subjects" />
                  <div className="ff full">
                    <label className="fl">Subjects {allSubjects.length === 0 && <span style={{fontWeight:400,color:'#b0b5cc',fontSize:'.64rem'}}>(add subjects to a class first)</span>}</label>
                    {allSubjects.length > 0
                      ? <>
                          <div className="subj-grid">
                            {allSubjects.map(s => (
                              <button key={s} type="button" className={`subj-btn${form.subjects.includes(s) ? ' on' : ''}`} onClick={() => toggleSubject(s)}>
                                {form.subjects.includes(s) ? '✓ ' : ''}{s}
                              </button>
                            ))}
                          </div>
                          {form.subjects.length > 0 && <span className="fh">Selected: {form.subjects.join(', ')}</span>}
                        </>
                      : <input className="fi" placeholder="Math, Physics…"
                          value={Array.isArray(form.subjects) ? form.subjects.join(', ') : form.subjects}
                          onChange={e => setForm(p=>({...p,subjects:e.target.value.split(',').map(s=>s.trim()).filter(Boolean)}))} />
                    }
                  </div>
                </div>
              </div>
              <div className="m-foot">
                <button type="button" className="btn btn-ghost" onClick={() => setModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-green" disabled={saving}>
                  <PaperAirplaneIcon />{saving ? 'Submitting…' : editing ? 'Submit Edit Request' : 'Submit Add Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      
      , document.body)}

      {/* ══ Assign Subjects & Classes Modal ══════════════════════════════════ */}
      {assign && createPortal(
        <div className="overlay" onClick={e => e.target === e.currentTarget && setAssign(null)}>
          <div className="modal" style={{maxWidth:460}}>
            <div className="m-hdr">
              <div className="m-hdr-l">
                <div className="m-icon blue"><ClipboardDocumentCheckIcon /></div>
                <div>
                  <div className="m-title">Request: Subjects & Class Assignment</div>
                  <div className="m-sub">{assign.user?.name} — which classes they TEACH in</div>
                </div>
              </div>
              <button className="m-close" onClick={() => setAssign(null)}><XMarkIcon /></button>
            </div>
            <form onSubmit={submitAssign} style={{display:'contents'}} autoComplete="off">
              <div className="m-body">
                <div className="info-box">
                  📚 This controls which classes appear in the teacher's portal. <strong>This is NOT the class teacher role</strong> — use the 🏠 button to set class teacher.
                </div>
                <div className="ff" style={{marginBottom:14}}>
                  <label className="fl">Subjects Taught {allSubjects.length === 0 && <span style={{fontWeight:400,color:'#b0b5cc',fontSize:'.64rem'}}>(add subjects to a class first)</span>}</label>
                  {allSubjects.length > 0
                    ? <><div className="subj-grid">{allSubjects.map(s => (
                            <button key={s} type="button" className={`subj-btn${assign.subjects.includes(s) ? ' on' : ''}`} onClick={() => toggleAssignSubject(s)}>
                              {assign.subjects.includes(s) ? '✓ ' : ''}{s}
                            </button>
                          ))}</div>
                        {assign.subjects.length > 0 && <span className="fh">Selected: {assign.subjects.join(', ')}</span>}</>
                    : <input className="fi" placeholder="Math, Science…"
                        value={assign.subjects.join(', ')}
                        onChange={e => setAssign(p => ({...p, subjects: e.target.value.split(',').map(s=>s.trim()).filter(Boolean)}))} />}
                </div>
                <div className="ff">
                  <label className="fl">Assigned Classes & Sections</label>
                  <div className="cls-list">
                    {classes.map(c => {
                      const id      = String(c._id);
                      const curSecs = assign.assignedSections?.[id];
                      const hasAny  = !!curSecs;
                      const isAll   = curSecs === 'all';
                      const sections = c.sections || [];
                      return (
                        <div key={id} className={`cls-item-wrap${hasAny ? ' has-on' : ''}`}>
                          <div className="cls-item-hdr" onClick={() => toggleAssignSection(c._id, 'all')}>
                            <div className="cls-chk" style={hasAny ? {background:'#10c98e',borderColor:'#10c98e'} : {}}>
                              <CheckIcon style={{width:10,height:10,strokeWidth:3,color:'white',opacity:hasAny?1:0}}/>
                            </div>
                            <span style={{flex:1,fontWeight:600,color:hasAny?'#0f7a5a':'#3a3f58'}}>{c.name}</span>
                            {hasAny && (
                              <span style={{fontSize:'.68rem',color:'#10c98e',fontWeight:600}}>
                                {isAll ? 'All sections' : (Array.isArray(curSecs) ? 'Sec ' + curSecs.join(', ') : '')}
                              </span>
                            )}
                          </div>
                          {hasAny && sections.length > 0 && (
                            <div className="cls-secs">
                              <button type="button" className={`sec-btn${isAll ? ' on-all' : ''}`} onClick={() => toggleAssignSection(c._id, 'all')}>All</button>
                              {sections.map(s => {
                                const secOn = Array.isArray(curSecs) && curSecs.includes(s);
                                return (
                                  <button key={s} type="button" className={`sec-btn${secOn ? ' on' : ''}`}
                                    onClick={e => { e.stopPropagation(); toggleAssignSection(c._id, s); }}>
                                    Section {s}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <span style={{fontSize:'.68rem',color:'#b0b5cc',marginTop:4,display:'block'}}>
                    Click a class to select it, then choose specific sections or leave as "All"
                  </span>
                </div>
              </div>
              <div className="m-foot">
                <button type="button" className="btn btn-ghost" onClick={() => setAssign(null)}>Cancel</button>
                <button type="submit" className="btn btn-blue" disabled={saving}>
                  <PaperAirplaneIcon />{saving ? 'Submitting…' : 'Submit Assignment Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      
      , document.body)}

      {/* ══ Class Teacher Modal ═══════════════════════════════════════════════ */}
      {ctModal && createPortal(
        <div className="overlay" onClick={e => e.target === e.currentTarget && setCtModal(null)}>
          <div className="modal" style={{maxWidth:440}}>
            <div className="m-hdr">
              <div className="m-hdr-l">
                <div className="m-icon teal"><HomeIcon /></div>
                <div>
                  <div className="m-title">Request: Set Class Teacher</div>
                  <div className="m-sub">{ctModal.teacher.user?.name} — responsible for attendance</div>
                </div>
              </div>
              <button className="m-close" onClick={() => setCtModal(null)}><XMarkIcon /></button>
            </div>
            <div className="m-body">
              <div className="info-box">
                🏠 The <strong>class teacher</strong> marks daily attendance. Each class can have only one class teacher. This request will be sent to admin for approval.
              </div>
              <div className="ff" style={{marginBottom:14}}>
                <label className="fl">Select Class</label>
                <div className="cls-list">
                  <label className={`cls-item${!ctModal.classId ? ' ct-on' : ''}`} onClick={() => setCtModal(p=>({...p,classId:'',section:''}))}>
                    <div className="ct-radio"><div className="ct-radio-dot"/></div>
                    <span style={{color:'#9094a8',fontStyle:'italic'}}>None — remove class teacher role</span>
                  </label>
                  {ctModal.allClasses.map(c => {
                    const isSelected    = ctModal.classId === String(c._id);
                    const currentCT     = c.classTeacher;
                    const ctName        = currentCT?.user?.name || currentCT?.name || null;
                    const isThisTeacher = currentCT && (currentCT._id || currentCT).toString() === ctModal.teacher._id.toString();
                    return (
                      <label key={c._id} className={`cls-item${isSelected ? ' ct-on' : ''}`}
                        onClick={() => setCtModal(p=>({...p,classId:String(c._id),section:c.sections?.[0]||''}))}>
                        <div className="ct-radio"><div className="ct-radio-dot"/></div>
                        <span style={{flex:1}}>{c.name}</span>
                        {currentCT && !isThisTeacher && ctName && (
                          <span style={{fontSize:'.66rem',color:'#f59e0b',fontWeight:600}}>⚠ {ctName}</span>
                        )}
                        {isThisTeacher && (
                          <span style={{fontSize:'.66rem',color:'#0d9488',fontWeight:600}}>✓ current</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
              {ctModal.classId && (
                <div className="ff">
                  <label className="fl">Section <span style={{fontWeight:400,textTransform:'none',fontSize:'.68rem',color:'#b0b5cc'}}>(optional — blank = all sections)</span></label>
                  <select className="fs" value={ctModal.section} onChange={e => setCtModal(p=>({...p,section:e.target.value}))}>
                    <option value="">All sections</option>
                    {(ctModal.allClasses.find(c => String(c._id) === ctModal.classId)?.sections || []).map(s => (
                      <option key={s} value={s}>Section {s}</option>
                    ))}
                  </select>
                </div>
              )}
              {ctModal.classId &&
                ctModal.allClasses.find(c => String(c._id) === ctModal.classId)?.classTeacher &&
                ![(ctModal.allClasses.find(c => String(c._id) === ctModal.classId)?.classTeacher?._id || '').toString()].includes(ctModal.teacher._id.toString()) && (
                  <div className="warn-box" style={{marginTop:12}}>
                    ⚠️ This class already has a class teacher. If approved, they will be replaced.
                  </div>
                )}
            </div>
            <div className="m-foot">
              <button className="btn btn-ghost" onClick={() => setCtModal(null)}>Cancel</button>
              <button className="btn btn-teal" onClick={submitClassTeacher} disabled={saving}>
                <PaperAirplaneIcon />{saving ? 'Submitting…' : ctModal.classId ? 'Submit Class Teacher Request' : 'Submit Remove Request'}
              </button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* ══ Exit Modal ════════════════════════════════════════════════════════ */}
      {exitModal && createPortal(
        <div className="overlay" onClick={e => e.target === e.currentTarget && setExitModal(null)}>
          <div className="modal" style={{maxWidth:440}}>
            <div className="m-hdr">
              <div className="m-hdr-l">
                <div className={`m-icon ${exitModal.exitType === 'resigned' ? 'amber' : 'red'}`}>
                  {exitModal.exitType === 'resigned' ? <ArrowRightOnRectangleIcon /> : <NoSymbolIcon />}
                </div>
                <div>
                  <div className="m-title">
                    {exitModal.exitType === 'resigned' ? 'Request: Mark as Resigned' : 'Request: Mark as Terminated'}
                  </div>
                  <div className="m-sub">{exitModal.teacher.user?.name}</div>
                </div>
              </div>
              <button className="m-close" onClick={() => setExitModal(null)}><XMarkIcon /></button>
            </div>
            <div className="m-body">
              <div className={`exit-warn${exitModal.exitType === 'terminated' ? ' red' : ''}`}>
                ⚠️ This request will be sent to admin for approval. Once approved, this teacher will be removed from all active lists. Historical records are preserved.
              </div>
              <div className="fg">
                <div className="ff full">
                  <label className="fl">{exitModal.exitType === 'resigned' ? 'Resignation Date' : 'Termination Date'}</label>
                  <input type="date" className="fi" value={exitForm.exitDate} onChange={e => setExitForm(p=>({...p,exitDate:e.target.value}))} />
                </div>
                <div className="ff full">
                  <label className="fl">Reason</label>
                  <textarea className="fi" rows={2} style={{resize:'vertical'}} required value={exitForm.exitNote}
                    onChange={e => setExitForm(p=>({...p,exitNote:e.target.value}))}
                    placeholder={exitModal.exitType === 'resigned' ? 'Reason for resignation…' : 'Reason for termination…'} />
                </div>
              </div>
            </div>
            <div className="m-foot">
              <button className="btn btn-ghost" onClick={() => setExitModal(null)}>Cancel</button>
              <button className={`btn ${exitModal.exitType === 'resigned' ? 'btn-amber' : 'btn-red'}`} onClick={submitExit} disabled={saving}>
                <PaperAirplaneIcon />
                {saving ? 'Submitting…' : exitModal.exitType === 'resigned' ? 'Submit Resignation Request' : 'Submit Termination Request'}
              </button>
            </div>
          </div>
        </div>

      , document.body)}

      <ConfirmModal
        open={!!statusModal}
        title={statusModal?.active ? 'Request: Activate Teacher' : 'Request: Deactivate Teacher'}
        subtitle={statusModal?.teacher?.user?.name}
        message={statusModal?.active
          ? 'Request activation for this teacher? Their login will be re-enabled once approved (or immediately, if direct action is on).'
          : 'Request deactivation for this teacher? They will lose portal access once approved (or immediately, if direct action is on). This is reversible.'}
        confirmLabel={statusModal?.active ? 'Request Activate' : 'Request Deactivate'}
        danger={!statusModal?.active}
        onConfirm={confirmStatusAction}
        onCancel={() => setStatusModal(null)}
        loading={statusSaving}
      />
    </>
  );
}